import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const VARIANTS: Record<Variant, string> = {
  // design docs/DESIGN-LIVE-ACTIVITY.md §3.2 — text-white on bg-ws-accent was
  // 2.8:1 in dark (fails AA). text-ws-on-accent is the token made for this
  // exact pairing and now clears 4.5:1 in all four themes (WIP-007 also
  // darkened --ws-accent itself, which was 4.32:1 in light — see
  // docs/AUDIT-ACCESSIBILITY.md #1). Danger now uses --ws-danger-solid +
  // --ws-on-danger (WIP-007) instead of bg-ws-danger text-white — the D8 gap
  // this comment used to flag: --ws-danger has to stay light in dark theme so
  // it still reads as TEXT on a dark surface, so the solid-fill button needs
  // its own darker background rather than reusing that token.
  primary:
    'bg-ws-accent text-ws-on-accent shadow-sm hover:bg-ws-accent-hover active:translate-y-px border border-transparent',
  secondary:
    'bg-ws-surface text-ws-ink border border-ws-border hover:bg-ws-surface-2 hover:border-ws-border-strong active:translate-y-px',
  ghost: 'bg-transparent text-ws-ink-2 hover:bg-ws-surface-2 hover:text-ws-ink border border-transparent active:translate-y-px',
  danger: 'bg-ws-danger-solid text-ws-on-danger shadow-sm hover:opacity-90 active:translate-y-px border border-transparent',
};

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-9 px-4 text-sm',
  lg: 'h-11 px-5 text-sm',
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, disabled, className = '', children, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent focus-visible:ring-offset-2 focus-visible:ring-offset-ws-bg disabled:opacity-50 disabled:cursor-not-allowed disabled:active:translate-y-0 ${VARIANTS[variant]} ${SIZES[size]} ${className}`}
      {...rest}
    >
      {loading && <Loader2 size={15} className="animate-spin" />}
      {children}
    </button>
  );
});

export default Button;
